import { useState } from "react";
import { Check, Loader2, Package, Truck, ShieldCheck, Box, CircleDot, MapPin } from "lucide-react";
import api from "@/lib/api";
import SEO from "@/components/SEO";

const STAGE_ICONS = {
  1: Check,
  2: Package,
  3: ShieldCheck,
  4: Box,
  5: Truck,
  6: MapPin,
};

function TimelineStage({ number, title, desc, currentStage, trackingNumber, carrier, trackingUrl }) {
  const isCompleted = number < currentStage;
  const isCurrent = number === currentStage;
  const isFuture = number > currentStage;
  const Icon = STAGE_ICONS[number] || CircleDot;

  return (
    <div data-testid={`stage-${number}`} className="flex gap-4 md:gap-6 relative">
      {/* Timeline line */}
      <div className="flex flex-col items-center">
        <div className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-500 ${
          isCompleted ? "bg-white text-black" :
          isCurrent ? "bg-white/10 border-2 border-white/60 text-white" :
          "bg-white/[0.03] border border-white/10 text-white/15"
        }`}>
          {isCompleted ? <Check size={18} strokeWidth={2.5} /> : <Icon size={18} />}
        </div>
        {number < 6 && (
          <div className={`w-px flex-1 min-h-[40px] my-2 transition-colors ${
            isCompleted ? "bg-white/30" : "bg-white/5"
          }`} />
        )}
      </div>
      {/* Content */}
      <div className={`pb-8 ${number === 6 ? "pb-0" : ""}`}>
        <h3 className={`text-sm font-['Inter'] font-medium transition-colors ${
          isCompleted ? "text-white/60" :
          isCurrent ? "text-white" :
          "text-white/15"
        }`}>
          {title}
          {isCurrent && <span className="inline-block w-1.5 h-1.5 bg-white rounded-full ml-2 animate-pulse align-middle" />}
        </h3>
        <p className={`text-[11px] font-['Inter'] mt-1 leading-relaxed ${
          isCompleted ? "text-white/25" :
          isCurrent ? "text-white/40" :
          "text-white/10"
        }`}>
          {desc}
        </p>
        {/* Tracking info on shipped stage */}
        {number === 5 && (isCurrent || isCompleted) && trackingNumber && (
          <div className="mt-3 p-3 bg-white/[0.03] border border-white/8">
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/30 font-['Inter'] mb-1">{carrier || "Carrier"}</p>
            <p className="text-sm font-['Inter'] text-white/70 font-mono">{trackingNumber}</p>
            {trackingUrl && (
              <a href={trackingUrl} target="_blank" rel="noopener noreferrer" data-testid="tracking-link"
                className="inline-block mt-2 text-[10px] uppercase tracking-[0.2em] text-white/40 hover:text-white/70 font-['Inter'] underline underline-offset-2 transition-colors">
                Track Package
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function TrackOrderPage() {
  const [orderNumber, setOrderNumber] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!orderNumber.trim() || !email.trim()) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.post("/track-order", { order_number: orderNumber.trim(), email: email.trim() });
      setResult(data);
    } catch (err) {
      const status = err.response?.status;
      if (status && status < 500) {
        setError("We couldn't find that order. Check the number and the email you used.");
      } else {
        setError("We couldn't check your order right now. Try again in a minute.");
      }
    } finally {
      setLoading(false);
    }
  };

  const stages = result?.stages || {};

  return (
    <div data-testid="track-order-page" className="bg-[#050505] min-h-screen">
      <SEO title="Track Your Order | AERWEAR" description="Track your AERWEAR order status." />
      <div className="grain-overlay" />

      <div className="max-w-xl mx-auto px-6 py-16 md:py-24">
        {/* Header */}
        <div className="mb-10 md:mb-14">
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/20 font-['Inter'] mb-3">Order Status</p>
          <h1 data-testid="page-title" className="text-4xl md:text-5xl font-['Bebas_Neue'] tracking-tight text-white leading-[0.9]">
            Track Your Order
          </h1>
        </div>

        {/* Lookup Form */}
        {!result && (
          <form onSubmit={handleSubmit} data-testid="track-form" className="space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-white/30 font-['Inter'] mb-2 block">Order Number</label>
              <input
                data-testid="order-number-input"
                type="text"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                placeholder="#1001"
                className="w-full bg-transparent border border-white/10 px-4 py-3.5 text-sm font-['Inter'] text-white/80 outline-none focus:border-white/30 placeholder:text-white/15 transition-colors"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-white/30 font-['Inter'] mb-2 block">Email</label>
              <input
                data-testid="email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="w-full bg-transparent border border-white/10 px-4 py-3.5 text-sm font-['Inter'] text-white/80 outline-none focus:border-white/30 placeholder:text-white/15 transition-colors"
              />
            </div>

            {error && (
              <p data-testid="error-message" className="text-sm text-white/50 font-['Inter'] py-2">{error}</p>
            )}

            <button
              data-testid="track-btn"
              type="submit"
              disabled={loading || !orderNumber.trim() || !email.trim()}
              className="w-full bg-white text-black py-4 text-[11px] uppercase tracking-[0.25em] font-semibold hover:bg-white/90 transition-colors disabled:opacity-30 disabled:cursor-not-allowed mt-2"
            >
              {loading ? <Loader2 size={16} className="animate-spin mx-auto" /> : "Track Order"}
            </button>
          </form>
        )}

        {/* Results */}
        {result && (
          <div data-testid="order-result" className="animate-fade-in">
            {/* Order Header */}
            <div className="flex items-start justify-between mb-8 pb-6 border-b border-white/5">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-white/25 font-['Inter']">Order</p>
                <p data-testid="result-order-number" className="text-lg font-['Inter'] text-white mt-1">{result.order_number}</p>
                {result.order_date && <p className="text-[11px] text-white/25 font-['Inter'] mt-1">Placed {result.order_date}</p>}
              </div>
              <button
                data-testid="track-another-btn"
                onClick={() => { setResult(null); setOrderNumber(""); setEmail(""); }}
                className="text-[10px] uppercase tracking-[0.2em] text-white/30 hover:text-white/60 font-['Inter'] transition-colors"
              >
                Track Another
              </button>
            </div>

            {/* Custom Note */}
            {result.custom_note && (
              <div data-testid="custom-note" className="mb-8 p-4 border border-white/8 bg-white/[0.02]">
                <p className="text-[10px] uppercase tracking-[0.2em] text-white/30 font-['Inter'] mb-2">Update</p>
                <p className="text-sm text-white/60 font-['Inter'] leading-relaxed">{result.custom_note}</p>
              </div>
            )}

            {/* Timeline */}
            <div className="mb-10">
              {[1, 2, 3, 4, 5, 6].map((num) => (
                <TimelineStage
                  key={num}
                  number={num}
                  title={stages[num]?.title || ""}
                  desc={stages[num]?.desc || ""}
                  currentStage={result.stage}
                  trackingNumber={result.tracking_number}
                  carrier={result.carrier}
                  trackingUrl={result.tracking_url}
                />
              ))}
            </div>

            {/* Estimated Delivery */}
            {result.estimated_delivery && result.stage < 6 && (
              <div className="mb-8 py-4 border-t border-b border-white/5">
                <p className="text-[10px] uppercase tracking-[0.2em] text-white/25 font-['Inter']">Estimated Delivery</p>
                <p data-testid="est-delivery" className="text-sm font-['Inter'] text-white/60 mt-1">{result.estimated_delivery}</p>
              </div>
            )}

            {/* Line Items */}
            {result.line_items?.length > 0 && (
              <div className="mb-8">
                <p className="text-[10px] uppercase tracking-[0.2em] text-white/25 font-['Inter'] mb-4">Items Ordered</p>
                <div className="space-y-3">
                  {result.line_items.map((item, i) => (
                    <div key={`item-${i}`} data-testid={`line-item-${i}`} className="flex items-center justify-between py-2 border-b border-white/[0.04]">
                      <div>
                        <p className="text-sm font-['Inter'] text-white/60">{item.name}</p>
                        {item.variant && <p className="text-[10px] text-white/20 font-['Inter'] mt-0.5">{item.variant}</p>}
                      </div>
                      <span className="text-[11px] text-white/30 font-['Inter']">x{item.quantity}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Total */}
            {result.total && (
              <div className="flex items-center justify-between pt-4 border-t border-white/5">
                <span className="text-[10px] uppercase tracking-[0.2em] text-white/30 font-['Inter']">Total</span>
                <span className="text-sm font-['Inter'] text-white/70">${result.total} {result.currency}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
